import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

interface EmailAdminProps {
  authToken: string;
}

interface UserWithEmail {
  id: string;
  username: string;
  email: string;
}

interface Recipient {
  email: string;
  username: string;
}

interface SendResult {
  total: number;
  successful: number;
  failed: number;
  results: Array<{
    email: string;
    username: string;
    success: boolean;
    error?: string;
  }>;
}

const EmailAdmin: React.FC<EmailAdminProps> = ({ authToken }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<UserWithEmail[]>([]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState<SendResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Click outside handler to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Debounced search
  const searchUsers = useCallback(
    async (query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        return;
      }

      setSearching(true);
      try {
        const response = await axios.get(
          `/api/admin/users/with-email?search=${encodeURIComponent(query)}`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
          },
        );
        setSearchResults(response.data);
        setShowDropdown(true);
      } catch (err) {
        console.error("Error searching users:", err);
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [authToken],
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);

    // Debounce search
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }
    searchTimeout.current = setTimeout(() => {
      searchUsers(value);
    }, 300);
  };

  const addRecipient = (user: UserWithEmail) => {
    // Check if already added
    if (recipients.some((r) => r.email === user.email)) {
      return;
    }
    setRecipients([
      ...recipients,
      { email: user.email, username: user.username },
    ]);
    setSearchQuery("");
    setSearchResults([]);
    setShowDropdown(false);
  };

  const removeRecipient = (email: string) => {
    setRecipients(recipients.filter((r) => r.email !== email));
  };

  const clearAll = () => {
    setRecipients([]);
    setSubject("");
    setContent("");
    setSendResult(null);
    setError(null);
  };

  const handleSend = async () => {
    if (recipients.length === 0) {
      setError("Please add at least one recipient");
      return;
    }
    if (!subject.trim()) {
      setError("Please enter a subject");
      return;
    }
    if (!content.trim()) {
      setError("Please enter email content");
      return;
    }

    setError(null);
    setSending(true);
    setSendResult(null);

    try {
      const response = await axios.post(
        "/api/admin/email/send",
        {
          recipients,
          subject: subject.trim(),
          content: content.trim(),
        },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      );
      setSendResult(response.data);

      // Clear form on success
      if (response.data.failed === 0) {
        setRecipients([]);
        setSubject("");
        setContent("");
      }
    } catch (err: any) {
      console.error("Error sending emails:", err);
      setError(
        err.response?.data?.message ||
          "Failed to send emails. Please try again.",
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <h3
        style={{
          fontSize: "20px",
          fontWeight: "600",
          color: "#e2e8f0",
          marginBottom: "24px",
        }}
      >
        Send Email to Users
      </h3>

      {/* Error message */}
      {error && (
        <div
          style={{
            backgroundColor: "#553c7b",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <p style={{ color: "#fbb6ce", margin: 0 }}>{error}</p>
        </div>
      )}

      {/* Send Result */}
      {sendResult && (
        <div
          style={{
            backgroundColor: sendResult.failed === 0 ? "#1e3a2f" : "#3a2f1e",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "20px",
            border: `1px solid ${sendResult.failed === 0 ? "#48bb78" : "#f59e0b"}`,
          }}
        >
          <p
            style={{
              color: sendResult.failed === 0 ? "#48bb78" : "#f59e0b",
              margin: 0,
              fontWeight: "600",
            }}
          >
            {sendResult.failed === 0
              ? `Successfully sent ${sendResult.successful} email(s)!`
              : `Sent ${sendResult.successful}/${sendResult.total} emails. ${sendResult.failed} failed.`}
          </p>
          {sendResult.failed > 0 && (
            <div style={{ marginTop: "12px" }}>
              <p
                style={{
                  color: "#a0aec0",
                  fontSize: "14px",
                  marginBottom: "8px",
                }}
              >
                Failed recipients:
              </p>
              {sendResult.results
                .filter((r) => !r.success)
                .map((r, i) => (
                  <div key={i} style={{ color: "#f87171", fontSize: "14px" }}>
                    {r.username} ({r.email}): {r.error}
                  </div>
                ))}
            </div>
          )}
        </div>
      )}

      <div
        style={{
          backgroundColor: "#2d3748",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #4a5568",
        }}
      >
        {/* User Search */}
        <div style={{ marginBottom: "24px" }}>
          <label
            style={{
              display: "block",
              color: "#a0aec0",
              fontSize: "14px",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Add Recipients
          </label>
          <div style={{ position: "relative" }} ref={dropdownRef}>
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearchChange}
              onFocus={() => searchResults.length > 0 && setShowDropdown(true)}
              placeholder="Search by username or email..."
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "8px",
                border: "1px solid #4a5568",
                backgroundColor: "#1a202c",
                color: "#e2e8f0",
                fontSize: "14px",
                outline: "none",
                boxSizing: "border-box",
              }}
            />
            {searching && (
              <span
                style={{
                  position: "absolute",
                  right: "12px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  color: "#a0aec0",
                }}
              >
                ...
              </span>
            )}

            {/* Dropdown */}
            {showDropdown && searchResults.length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  right: 0,
                  backgroundColor: "#1a202c",
                  border: "1px solid #4a5568",
                  borderRadius: "8px",
                  marginTop: "4px",
                  maxHeight: "200px",
                  overflowY: "auto",
                  zIndex: 10,
                }}
              >
                {searchResults.map((user) => {
                  const isAdded = recipients.some(
                    (r) => r.email === user.email,
                  );
                  return (
                    <div
                      key={user.id}
                      onClick={() => !isAdded && addRecipient(user)}
                      style={{
                        padding: "12px 16px",
                        cursor: isAdded ? "default" : "pointer",
                        borderBottom: "1px solid #2d3748",
                        backgroundColor: isAdded ? "#2d3748" : "transparent",
                        opacity: isAdded ? 0.5 : 1,
                        transition: "background-color 0.2s",
                      }}
                      onMouseEnter={(e) => {
                        if (!isAdded) {
                          e.currentTarget.style.backgroundColor = "#2d3748";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isAdded) {
                          e.currentTarget.style.backgroundColor = "transparent";
                        }
                      }}
                    >
                      <div style={{ color: "#9146FF", fontWeight: "500" }}>
                        {user.username}
                        {isAdded && (
                          <span style={{ color: "#48bb78", marginLeft: "8px" }}>
                            (added)
                          </span>
                        )}
                      </div>
                      <div style={{ color: "#718096", fontSize: "12px" }}>
                        {user.email}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Recipients List */}
        {recipients.length > 0 && (
          <div style={{ marginBottom: "24px" }}>
            <label
              style={{
                display: "block",
                color: "#a0aec0",
                fontSize: "14px",
                marginBottom: "8px",
                fontWeight: "500",
              }}
            >
              Recipients ({recipients.length})
            </label>
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "8px",
                padding: "12px",
                backgroundColor: "#1a202c",
                borderRadius: "8px",
                border: "1px solid #4a5568",
              }}
            >
              {recipients.map((recipient) => (
                <div
                  key={recipient.email}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "6px 12px",
                    backgroundColor: "#4a5568",
                    borderRadius: "20px",
                  }}
                >
                  <span style={{ color: "#e2e8f0", fontSize: "14px" }}>
                    {recipient.username}
                  </span>
                  <button
                    onClick={() => removeRecipient(recipient.email)}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#a0aec0",
                      cursor: "pointer",
                      padding: "0",
                      fontSize: "16px",
                      lineHeight: 1,
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = "#f87171";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = "#a0aec0";
                    }}
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Subject */}
        <div style={{ marginBottom: "24px" }}>
          <label
            style={{
              display: "block",
              color: "#a0aec0",
              fontSize: "14px",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Enter email subject..."
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              backgroundColor: "#1a202c",
              color: "#e2e8f0",
              fontSize: "14px",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Content */}
        <div style={{ marginBottom: "24px" }}>
          <label
            style={{
              display: "block",
              color: "#a0aec0",
              fontSize: "14px",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Content
          </label>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Enter email content... (plain text, line breaks will be preserved)"
            rows={10}
            style={{
              width: "100%",
              padding: "12px 16px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              backgroundColor: "#1a202c",
              color: "#e2e8f0",
              fontSize: "14px",
              outline: "none",
              resize: "vertical",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
          />
        </div>

        {/* Actions */}
        <div
          style={{ display: "flex", gap: "12px", justifyContent: "flex-end" }}
        >
          <button
            onClick={clearAll}
            style={{
              padding: "12px 24px",
              borderRadius: "8px",
              border: "1px solid #4a5568",
              backgroundColor: "transparent",
              color: "#a0aec0",
              fontSize: "14px",
              fontWeight: "500",
              cursor: "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "#718096";
              e.currentTarget.style.color = "#e2e8f0";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "#4a5568";
              e.currentTarget.style.color = "#a0aec0";
            }}
          >
            Clear All
          </button>
          <button
            onClick={handleSend}
            disabled={sending || recipients.length === 0}
            style={{
              padding: "12px 24px",
              borderRadius: "8px",
              border: "none",
              backgroundColor:
                sending || recipients.length === 0 ? "#4a5568" : "#9146FF",
              color: sending || recipients.length === 0 ? "#718096" : "#fff",
              fontSize: "14px",
              fontWeight: "600",
              cursor:
                sending || recipients.length === 0 ? "not-allowed" : "pointer",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              if (!sending && recipients.length > 0) {
                e.currentTarget.style.backgroundColor = "#7c3aed";
              }
            }}
            onMouseLeave={(e) => {
              if (!sending && recipients.length > 0) {
                e.currentTarget.style.backgroundColor = "#9146FF";
              }
            }}
          >
            {sending
              ? "Sending..."
              : `Send Email${recipients.length > 1 ? "s" : ""}`}
          </button>
        </div>
      </div>
    </div>
  );
};

export default React.memo(EmailAdmin);
